/*
 * @Description:
 * @Author: zhanjunwei
 * @Date: 2024-12-04 17:33:51
 * @LastEditors: zhanjunwei
 * @LastEditTime: 2025-01-16 16:48:09
 */
'use client';
import * as coral from '@ssv-lab/sdk-ssv-coral';
import { getClientUserInfoApi } from './useApi';
import { IServiceChooseIdentityProps } from '@ssv-lab/sdk-ssv-coral';
import { LoadingPlugin } from 'tdesign-react';
import { sleep } from './useUtils';
import { getSafeLoginRedirectUrl } from './useMobileLogin';

const getLoginBackWhiteOrigin = () => {
  return [window.location.origin];
};

const appid = process.env.NEXT_PUBLIC_CORAL_APPID;
const beacon = process.env.NEXT_PUBLIC_DATONG_ID;
const basepath = process.env.NEXT_PUBLIC_BASE_PATH;

coral.init({
  appid,
  host: basepath,
  beacon,
});

// 跳转到上一个页面
export const redirectFromUrl = ({
  from,
  skip,
  queryData = {},
}: { from?: string; skip?: '1'; queryData?: Record<string, any> } = {}) => {
  // 判断当前是中文还是英文网站
  const hostname = window.location.hostname;
  const isEnSite = hostname.startsWith('en.') || hostname.startsWith('en-');

  const { searchParams } = new URL(window.location.href);
  const sUrl = searchParams?.get('s_url');

  // 如果有 s_url 参数，优先使用（支持中文网站的活动报名页跳转）
  if (sUrl) {
    const redirectUrl = new URL(
      getSafeLoginRedirectUrl(sUrl, window.location.origin),
      window.location.origin
    );
    from && redirectUrl.searchParams.set('s_from', from);
    skip && redirectUrl.searchParams.set('s_realname_skip', skip);
    Object.keys(queryData).forEach((key) => {
      redirectUrl.searchParams.set(key, queryData[key]);
    });
    window.location.replace(redirectUrl);
    return;
  }

  // 英文网站使用原有逻辑
  let url: string;
  if (isEnSite) {
    // 英文网站默认跳转到首页 /case/activity/s-2026-signup/
    url = process.env.NEXT_PUBLIC_APP_SERVER_URL
      ? process.env.NEXT_PUBLIC_APP_SERVER_URL + '/case/activity/s-2026-signup/'
      : '/case/activity/s-2026-signup/';
  } else {
    // 中文网站强制跳转到 test.ssv.tencent.com 或 ssv.tencent.com
    const isProduction = hostname === 'ssv.tencent.com';
    url = isProduction
      ? 'https://ssv.tencent.com/'
      : 'https://test.ssv.tencent.com/';
  }

  // 解码
  const s_url = decodeURIComponent(url);
  // 跳转链接
  let redirectUrl = new URL(s_url);
  // 修复安全工单，增加登录成功后白名单验证，非白名单兜底服务地址
  const loginBackWhiteOrigin = getLoginBackWhiteOrigin();
  if (!loginBackWhiteOrigin.includes(redirectUrl.origin)) {
    console.error('跳转链接和当前链接不一致，跳转到默认链接');
    redirectUrl = new URL(process.env.NEXT_PUBLIC_APP_SERVER_URL || '/');
  }
  from && redirectUrl.searchParams.set('s_from', from);
  skip && redirectUrl.searchParams.set('s_realname_skip', skip);
  Object.keys(queryData).forEach((key) => {
    redirectUrl.searchParams.set(key, queryData[key]);
  });
  window.location.replace(redirectUrl);
};

// 重写登录ts类型
type IServiceLoginProps = Omit<coral.IServiceLoginProps, 'onSuccess'>;
interface ShowServiceLoginParams extends IServiceLoginProps {
  onSuccess?: () => void;
  isCheckRealName?: boolean;
}

export const showServiceLogin = (params: ShowServiceLoginParams = {}) => {
  const { theme, isCheckRealName = true, ...otherProps } = params;
  const closeBtn = theme !== 'embed';

  const p = new Promise((resolve) => {
    coral.showServiceLogin({
      appid,
      beacon,
      theme,
      closeBtn,
      ...otherProps,
      onSuccess: async () => {
        if (!isCheckRealName) {
          return resolve(true);
        }

        // 验证实名
        const { data } = await getClientUserInfoApi();
        const { hasTeam, info, real_status } = data;
        // 获取用户是否需要跳过实名
        const id = `realname_skip_${info.GUID}`;
        const isSkipRealName = localStorage.getItem(id) === '1';

        // 如果是有团队并且已经实名，则登录的时候选择团队
        if (hasTeam && real_status) {
          showChangeTeamModal({ isLogin: true });
          return;
        }

        // 用户没有实名，但已经跳过实名，且已经有团队
        if (isSkipRealName && hasTeam) {
          showChangeTeamModal({ isLogin: true });
          return;
        }

        if (isSkipRealName || real_status) {
          redirectFromUrl({ from: 'coral_login' });
        } else {
          // 实名
          await showServiceRealName({
            closeBtn: false,
            isRedirectFromUrl: !hasTeam, // 如果有团队，则不直接返回上一级页面
          });
          // 实名后，选择团队
          if (hasTeam) {
            showChangeTeamModal({ isLogin: true });
          }
        }
      },
    });
  });
  return p;
};

interface ShowServiceRealNameParams {
  onSkip?: () => void;
  onSuccess?: () => void;
  skip?: boolean;
  preview?: boolean;
  [key: string]: any; // 允许其他任意属性
}

// 重写实名ts类型
type IServiceRealnameProps = Omit<coral.IServiceRealnameProps, 'onSuccess'>;
interface ShowServiceRealNameParams extends IServiceRealnameProps {
  onSuccess?: () => void;
  isRedirectFromUrl?: boolean;
}

export const showServiceRealName = (params: ShowServiceRealNameParams = {}) => {
  const {
    onSkip,
    onSuccess,
    skip = true,
    preview = false,
    isRedirectFromUrl = true,
    ...otherProps
  } = params;
  const p = new Promise((resolve, reject) => {
    coral.showServiceRealname({
      appid,
      beacon,
      preview,
      skip,
      ...otherProps,
      onSkip: () => {
        if (!isRedirectFromUrl) {
          return resolve('skip');
        }
        redirectFromUrl({ from: 'coral_realname', skip: '1' });
      },
      onSuccess: async () => {
        if (!isRedirectFromUrl) {
          return resolve('success');
        }
        redirectFromUrl({ from: 'coral_realname' });
      },
    });
  });
  return p;
};

const replace = (href: string) => {
  window.location.replace(href);
};
type IdentityMode = IServiceChooseIdentityProps['mode'];

export { coral };

// 切换团队

/**
 * @description 在申领、团队信息详情。团队表单编辑页切换团队时，跳转到账号中心
 */
const config = [
  '/open-platform/tool-apply',
  '/account',
  '/team/detail',
  '/team/authentication',
  '/team-account',
];
export const showChangeTeamModal = ({
  isLogin = false,
  redirectUrl,
  closeBtn = false,
  ...otherProps
}: {
  isLogin: boolean;
  redirectUrl?: string;
  closeBtn?: boolean;
  [key: string]: any;
}) => {
  const modal = coral.showServiceChooseIdentity({
    ...otherProps,
    closeBtn,
    mode: 'all' as IdentityMode,
    onChooseIdentity(data) {
      const loading = LoadingPlugin({ loading: true });
      // // 如果是登录页打开切换团队，则跳转回上一个页面
      if (isLogin) {
        redirectFromUrl({ from: 'coral_login' });
        modal.close();
        return;
      }
      // 重定向地址
      if (redirectUrl) {
        replace(redirectUrl);
        return;
      }
      const { pathname } = window.location;

      const isToAccout = config.some((v) => {
        return pathname.startsWith(v);
      });

      if (isToAccout) {
        replace(window.location.origin + '/team-account');
      } else {
        window.location.reload();
      }

      sleep(500).then(() => {
        loading.hide();
      });
      modal.close();
    },
    onCreateOrganization() {
      const loading = LoadingPlugin({ loading: true });
      window.open('/team/preposition');
      loading.hide();
    },
  });
};

const defaultUrl =
  (typeof window !== 'undefined'
    ? window?.location.href
    : process.env.NEXT_PUBLIC_APP_SERVER_URL) || '/';

export const mobilePaths = ({
  redirectUrl = '',
}: {
  [key: string]: string;
}) => {
  const { pathname } = window.location;
  const isToAccout = config.some((v) => {
    return pathname.startsWith(v);
  });
  const url = redirectUrl || (isToAccout ? '/team-account' : defaultUrl);
  // 路由参数
  const searchParams = `?s_appid=${appid}&s_beacon=${beacon}&s_url=${encodeURIComponent(
    url
  )}`;

  // 路由地址
  const values = {
    teamPath: `/embed/page/mobile/choose_identity.html${searchParams}`,
  };
  return values;
};
